import { z } from 'zod';
import { employmentApi } from '../../../api';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../constants/employmentEventTypes';
import { EVENT_ACTOR_TYPES } from '../../../../constants/employmentActorTypes';
import { employmentEpisodeIdSchema, withEpisode } from '../episodeAddress';

/**
 * Validation schema for hire withdrawal
 */
const withdrawHireSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6
  reason: z.string().min(1, 'Reason is required'),
  // ADR-136 phase 5 (§2a settlement #3): true when the joiner has a scheduled
  // contract end — the void batch cancels it FIRST via the production
  // resign.withdrawn leg, otherwise the orphaned Pending TERMINATION_FINALIZED
  // would later be enacted by the sweep against a voided person.
  withdrawScheduledEnd: z.boolean().optional()
});

/**
 * Withdraw Hire - Withdraw Pending Hire
 *
 * @param {Object} payload - Withdrawal data
 * @param {string} payload.employeeId - Required
 * @param {string} payload.reason - Required
 * @param {boolean} [payload.withdrawScheduledEnd] - ADR-136: void an FTC joiner
 *   as the batch [resign.withdrawn, hire.withdrawn] — one transaction, full trail.
 * @param {string} [actorType='User'] - Actor type (System, User, Import)
 * @returns {Promise<Object>}
 * @throws {z.ZodError} - If validation fails
 */
export const withdrawHire = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  // Validate payload
  const validatedPayload = withdrawHireSchema.parse(payload);

  const events = [];

  if (validatedPayload.withdrawScheduledEnd) {
    events.push({
      type: EMPLOYMENT_EVENT_TYPES.RESIGNATION_WITHDRAWN,
      payload: {
        employeeId: validatedPayload.employeeId,
      ...withEpisode(validatedPayload),
        reason: `Scheduled contract end withdrawn — hire withdrawn. ${validatedPayload.reason}`
      }
    });
  }

  events.push({
    type: EMPLOYMENT_EVENT_TYPES.WITHDRAWN,
    payload: {
      employeeId: validatedPayload.employeeId,
      ...withEpisode(validatedPayload),
      reason: validatedPayload.reason
    }
  });

  return await employmentApi.triggerEmploymentEvents({
    metadata: {
      actorType
    },
    events
  });
};
